import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  AvatarGroup,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add,
  Group,
  Badge,
  Analytics,
  Edit,
  Delete,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "@/store";
import { fetchTeams, createTeam, deleteTeam } from "@/store/slices/teamSlice";
import { useNotification } from "@/context/NotificationContext";
import { PAGE_MAX_WIDTH, LABEL_CAPS_SX } from "@/constants";
import { PageLoading } from "@/components/common/PageLoading";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyTableRow } from "@/components/common/EmptyTableRow";
import { TableHeaderCell } from "@/components/common/TableHeaderCell";

export function TeamsPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { list, loading } = useAppSelector((s) => s.teams);
  const { notify } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    dispatch(fetchTeams());
  }, [dispatch]);

  const handleCreate = async () => {
    await dispatch(
      createTeam({ name, description: description || undefined }),
    ).unwrap();
    notify(t("common.success"), "success");
    setName("");
    setDescription("");
    setDialogOpen(false);
  };

  const totalMembers = list.reduce(
    (sum: number, team: any) => sum + (team.userTeams?.length || 0),
    0,
  );
  const avgSize =
    list.length > 0 ? (totalMembers / list.length).toFixed(1) : "0";

  if (loading) return <PageLoading />;

  return (
    <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
      <PageHeader
        title={t("teams.title")}
        subtitle={t("teams.subtitle")}
        action={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            {t("teams.createTeam")}
          </Button>
        }
      />
      <StatsBar
        totalTeams={list.length}
        totalMembers={totalMembers}
        avgSize={avgSize}
      />
      <Card variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableHeaderCell>{t("teams.teamName")}</TableHeaderCell>
                <TableHeaderCell>{t("common.description")}</TableHeaderCell>
                <TableHeaderCell>{t("teams.members")}</TableHeaderCell>
                <TableHeaderCell>{t("teams.createdAt")}</TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("common.actions")}
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((team: any) => (
                <TableRow key={team.id} hover>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "primary.main",
                          fontWeight: 700,
                          fontSize: 14,
                        }}
                      >
                        {team.name.substring(0, 2).toUpperCase()}
                      </Avatar>
                      <Typography
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {team.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: "text.secondary",
                        maxWidth: 250,
                      }}
                    >
                      {team.description || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AvatarGroup
                        max={3}
                        sx={{
                          "& .MuiAvatar-root": {
                            width: 28,
                            height: 28,
                            fontSize: 11,
                          },
                        }}
                      >
                        {(team.userTeams || []).map((ut: any) => (
                          <Avatar key={ut.userId}>
                            {ut.user?.username?.[0] || "?"}
                          </Avatar>
                        ))}
                      </AvatarGroup>
                      <Typography variant="body2">
                        {team.userTeams?.length || 0} {t("teams.members")}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(team.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => dispatch(deleteTeam(team.id))}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && <EmptyTableRow colSpan={5} />}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("teams.createTeam")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label={t("teams.teamName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            margin="dense"
          />
          <TextField
            label={t("common.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!name.trim()}
          >
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function StatsBar({
  totalTeams,
  totalMembers,
  avgSize,
}: {
  totalTeams: number;
  totalMembers: number;
  avgSize: string;
}) {
  const { t } = useTranslation();
  const stats = [
    {
      label: t("teams.totalTeams"),
      value: totalTeams,
      icon: <Group color="primary" />,
    },
    {
      label: t("teams.totalMembers"),
      value: totalMembers,
      icon: <Badge sx={{ color: "warning.main" }} />,
    },
    {
      label: t("teams.avgTeamSize"),
      value: avgSize,
      icon: <Analytics color="secondary" />,
    },
    {
      label: t("teams.activeProjects"),
      value: "-",
      icon: <Analytics sx={{ color: "success.main" }} />,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        gap: 2,
        mb: 4,
      }}
    >
      {stats.map((stat) => (
        <Card key={stat.label} variant="outlined">
          <CardContent>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography
                variant="caption"
                sx={[
                  {
                    color: "text.secondary",
                  },
                  ...(Array.isArray(LABEL_CAPS_SX)
                    ? LABEL_CAPS_SX
                    : [LABEL_CAPS_SX]),
                ]}
              >
                {stat.label}
              </Typography>
              {stat.icon}
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
              }}
            >
              {stat.value}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
